import { useRef, useState, useEffect, useCallback } from 'react';
import { Heart, Sparkles } from 'lucide-react';
import confetti from 'canvas-confetti';
import { SCRATCH_CARD, MY_NAME } from '@/config/valentineContent';

export default function ScratchCard() {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [revealed, setRevealed] = useState(false);
  const [scratching, setScratching] = useState(false);
  const [scratchPct, setScratchPct] = useState(0);
  const [sparkles, setSparkles] = useState([]);
  const scratchingRef = useRef(false);
  const sparkleIdRef = useRef(0);
  const animFrameRef = useRef(null);

  const hiddenMessage = SCRATCH_CARD.hiddenMessage;

  const drawHolographic = useCallback((ctx, w, h) => {
    // Read theme-aware colors
    const styles = getComputedStyle(document.documentElement);
    const accentLight = styles.getPropertyValue('--accent-light').trim() || '#F48FB1';
    const accentPrimary = styles.getPropertyValue('--accent-primary').trim() || '#E91E63';

    const g = ctx.createLinearGradient(0, 0, w, h);
    g.addColorStop(0, accentLight);
    g.addColorStop(0.25, accentPrimary);
    g.addColorStop(0.4, '#F0F0F0');
    g.addColorStop(0.5, accentLight);
    g.addColorStop(0.6, '#F5F5F5');
    g.addColorStop(0.75, accentPrimary);
    g.addColorStop(1, accentLight);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);

    // Subtle noise/texture
    for (let i = 0; i < 3000; i++) {
      const x = Math.random() * w;
      const y = Math.random() * h;
      ctx.fillStyle = `rgba(255,255,255,${Math.random() * 0.15})`;
      ctx.fillRect(x, y, 1, 1);
    }

    // Scratch hint text
    ctx.font = "bold 13px 'Lato', sans-serif";
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('Scratch here', w / 2, h / 2 - 10);

    // Scratch hint icon (small circles for sparkle)
    ctx.font = "20px serif";
    ctx.fillText('✦', w / 2, h / 2 + 14);

    // Diagonal shimmer lines
    ctx.strokeStyle = 'rgba(255,255,255,0.08)';
    ctx.lineWidth = 1;
    for (let i = -h; i < w + h; i += 20) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i - h, h);
      ctx.stroke();
    }
  }, []);

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const rect = container.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;
    const ctx = canvas.getContext('2d');
    drawHolographic(ctx, canvas.width, canvas.height);
  }, [drawHolographic]);

  useEffect(() => {
    if (!revealed) {
      const timer = setTimeout(initCanvas, 150);
      return () => clearTimeout(timer);
    }
  }, [revealed, initCanvas]);

  // Redraw canvas on theme change
  useEffect(() => {
    if (revealed) return;
    const observer = new MutationObserver(() => { initCanvas(); });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, [revealed, initCanvas]);

  // Animated shimmer overlay
  useEffect(() => {
    if (revealed) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    let offset = 0;
    const animate = () => {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      // Draw a subtle shimmer band moving across
      ctx.save();
      ctx.globalCompositeOperation = 'source-atop';
      const g = ctx.createLinearGradient(offset - 200, 0, offset, canvas.height);
      g.addColorStop(0, 'rgba(255,255,255,0)');
      g.addColorStop(0.5, 'rgba(255,255,255,0.12)');
      g.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.restore();
      offset += 1.5;
      if (offset > canvas.width + 200) offset = -200;
      animFrameRef.current = requestAnimationFrame(animate);
    };
    // Start after a short delay
    const t = setTimeout(() => {
      animFrameRef.current = requestAnimationFrame(animate);
    }, 500);
    return () => {
      clearTimeout(t);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [revealed]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return {
      x: (touch.clientX - rect.left) * (canvas.width / rect.width),
      y: (touch.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const scratch = (x, y) => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    ctx.globalCompositeOperation = 'destination-out';
    // Larger, softer brush
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, 28);
    gradient.addColorStop(0, 'rgba(0,0,0,1)');
    gradient.addColorStop(0.7, 'rgba(0,0,0,0.8)');
    gradient.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(x, y, 28, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
  };

  const spawnSparkle = (clientX, clientY) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const sx = clientX - rect.left;
    const sy = clientY - rect.top;
    const newSparkles = Array.from({ length: 3 }, () => ({
      id: ++sparkleIdRef.current,
      x: sx + (Math.random() - 0.5) * 30,
      y: sy + (Math.random() - 0.5) * 30,
      size: Math.random() * 6 + 3,
    }));
    setSparkles(prev => [...prev, ...newSparkles]);
    setTimeout(() => {
      setSparkles(prev => prev.filter(s => !newSparkles.includes(s)));
    }, 600);
  };

  const calcPercentage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return 0;
    const ctx = canvas.getContext('2d');
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    let cleared = 0;
    // Sample every 4th pixel for performance
    for (let i = 3; i < data.length; i += 16) {
      if (data[i] === 0) cleared++;
    }
    return cleared / (data.length / 16);
  };

  const doReveal = useCallback(() => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    setRevealed(true);
    confetti({
      particleCount: 60,
      spread: 70,
      origin: { y: 0.65 },
      colors: ['#E91E63', '#F48FB1', '#FF80AB', '#FCE4EC', '#ffffff'],
    });
  }, []);

  const onStart = (e) => {
    scratchingRef.current = true;
    setScratching(true);
    const p = getPos(e);
    scratch(p.x, p.y);
    const touch = e.touches ? e.touches[0] : e;
    spawnSparkle(touch.clientX, touch.clientY);
  };

  const onMove = (e) => {
    if (!scratchingRef.current) return;
    e.preventDefault();
    const p = getPos(e);
    scratch(p.x, p.y);
    const touch = e.touches ? e.touches[0] : e;
    spawnSparkle(touch.clientX, touch.clientY);
  };

  const onEnd = () => {
    scratchingRef.current = false;
    setScratching(false);
    const pct = calcPercentage();
    setScratchPct(Math.round(pct * 100));
    if (pct > 0.40) doReveal();
  };

  return (
    <section id="scratch-card" data-testid="scratch-card-section" className="section-romantic text-center">
      <div className="max-w-sm mx-auto">
        {/* Header */}
        <div className="mb-6">
          <p className="font-body text-[10px] tracking-widest uppercase mb-2" style={{ color: 'var(--accent-light)' }}>
            A hidden message awaits
          </p>
          <h2 className="font-heading text-2xl md:text-3xl" style={{ color: 'var(--text-primary)' }}>
            {SCRATCH_CARD.heading}
          </h2>
        </div>

        {/* Scratch ticket frame */}
        <div className="scratch-ticket-frame" style={{ borderColor: 'var(--accent-light)' }}>
          {/* Ticket header */}
          <div className="scratch-ticket-header" style={{ background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-light))' }}>
            <div className="flex items-center justify-between px-4 py-2">
              <div className="flex items-center gap-1.5">
                <Heart size={12} fill="#fff" strokeWidth={0} />
                <span className="font-heading text-[10px] text-white tracking-widest uppercase">{SCRATCH_CARD.ticketLabel}</span>
              </div>
              <span className="font-body text-[10px] text-white opacity-70">{SCRATCH_CARD.ticketNumber}</span>
            </div>
          </div>

          {/* Main scratch area */}
          <div
            ref={containerRef}
            className="scratch-ticket-body relative overflow-hidden"
            style={{ backgroundColor: 'var(--bg-card)', height: 200 }}
          >
            {/* Hidden message underneath */}
            <div className={`absolute inset-0 flex flex-col items-center justify-center p-8 transition-all duration-700 ${revealed ? 'scratch-msg-revealed' : ''}`}>
              <Sparkles size={20} className="mb-3" style={{ color: 'var(--accent-primary)' }} />
              <p className="font-heading text-base md:text-lg leading-relaxed text-center" style={{ color: 'var(--text-primary)' }}>
                {hiddenMessage}
              </p>
              <p className="font-cursive text-lg mt-3" style={{ color: 'var(--accent-primary)' }}>
                — {MY_NAME}
              </p>
            </div>

            {/* Canvas scratch layer */}
            {!revealed && (
              <canvas
                ref={canvasRef}
                data-testid="scratch-canvas"
                className="absolute inset-0 w-full h-full scratch-canvas"
                onMouseDown={onStart}
                onMouseMove={onMove}
                onMouseUp={onEnd}
                onMouseLeave={onEnd}
                onTouchStart={onStart}
                onTouchMove={onMove}
                onTouchEnd={onEnd}
              />
            )}

            {/* Sparkle particles while scratching */}
            {sparkles.map(s => (
              <span
                key={s.id}
                className="scratch-sparkle"
                style={{
                  left: s.x,
                  top: s.y,
                  fontSize: s.size,
                  color: 'var(--accent-primary)',
                }}
              >
                ✦
              </span>
            ))}
          </div>

          {/* Ticket footer */}
          <div className="scratch-ticket-footer px-4 py-2.5 flex items-center justify-between" style={{ borderColor: 'var(--accent-glow)' }}>
            {!revealed ? (
              <>
                {/* Scratch progress */}
                <div className="flex items-center gap-2">
                  <div className="w-20 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--accent-glow)' }}>
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{
                        width: `${scratchPct}%`,
                        background: 'linear-gradient(90deg, var(--accent-light), var(--accent-primary))',
                      }}
                    />
                  </div>
                  <span className="font-body text-[10px]" style={{ color: 'var(--text-secondary)' }}>
                    {scratchPct > 0 ? `${scratchPct}%` : ''}
                  </span>
                </div>
                <span className="font-body text-[10px]" style={{ color: scratching ? 'var(--accent-primary)' : 'var(--text-secondary)', transition: 'color 0.3s' }}>
                  {scratching ? 'Scratching...' : 'Use finger or mouse'}
                </span>
              </>
            ) : (
              <div className="w-full text-center">
                <span className="font-body text-xs" style={{ color: 'var(--accent-primary)' }}>
                  Message unlocked!
                </span>
              </div>
            )}
          </div>

          {/* Perforated edge decoration (top) */}
          <div className="scratch-perforation scratch-perforation-top" />
          <div className="scratch-perforation scratch-perforation-bottom" />
        </div>

        {/* Reveal fallback */}
        {!revealed && (
          <button
            data-testid="scratch-reveal-btn"
            onClick={doReveal}
            className="mt-5 px-6 py-2 rounded-xl font-body text-xs transition-all duration-300 hover:scale-105"
            style={{ color: 'var(--accent-primary)', border: '1px solid var(--accent-light)' }}
          >
            Or tap to reveal
          </button>
        )}
      </div>
    </section>
  );
}
